import { PublicApiBuild } from '@/models/Build';

export const buildLogSummaryPath = (appSlug: string, buildSlug: PublicApiBuild['slug']) =>
  `/v0.1/apps/${appSlug}/builds/${buildSlug}/log/summary`;

export const stepLogPath = (appSlug: string, buildSlug: string, stepUuid: string) =>
  `/v0.1/apps/${appSlug}/builds/${buildSlug}/log/steps/${stepUuid}`;

export const appBuildsPath = (appSlug: string, branch?: string, workflow?: string, status?: number) => {
  const url = `/v0.1/apps/${appSlug}/builds`;

  const searchParams = new URLSearchParams();

  if (branch) {
    searchParams.append('branch', branch);
  }

  if (workflow) {
    searchParams.append('workflow', workflow);
  }

  if (status !== undefined) {
    searchParams.append('status', String(status));
  }

  const paramString = searchParams.toString();

  return paramString ? `${url}?${paramString}` : url;
};

export const buildArtifactsPath = (appSlug: string, buildSlug: string, nextSlug?: string) => {
  const url = `/v0.1/apps/${appSlug}/builds/${buildSlug}/artifacts`;

  const searchParams = new URLSearchParams();

  if (nextSlug) {
    searchParams.append('next', nextSlug);
  }

  const paramString = searchParams.toString();

  return paramString ? `${url}?${paramString}` : url;
};

export const appBranchesPath = (appSlug: string, filter = '', limit = 0) => {
  const url = `/v0.1/apps/${appSlug}/pipelines/active-branches`;
  const searchParams = new URLSearchParams();

  if (limit) {
    searchParams.append('limit', String(limit));
  }

  if (filter) {
    searchParams.append('branch', String(filter));
  }

  const paramString = searchParams.toString();

  return paramString ? `${url}?${paramString}` : url;
};

export const awsControllersPath = (slug: string) => {
  return `/v0.1/platform/organization/${slug}/aws_controllers`;
};

export const awsControllerPath = (workspaceSlug: string, awsControllerId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_controllers/${awsControllerId}`;
};

export const agentPoolsPath = (slug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/v0.1/platform/organization/${slug}/agent_pools${queryParam}`;
};

export const agentPoolPath = (slug: string, agentPoolId: string) => {
  return `/v0.1/platform/organization/${slug}/agent_pools/${agentPoolId}`;
};

export const awsMachinePoolsPath = (slug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/v0.1/platform/organization/${slug}/aws_machine_pools${queryParam}`;
};

export const awsMachinePoolPath = (workspaceSlug: string, poolId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_machine_pools/${poolId}`;
};

export const validateAwsAmiPath = (workspaceSlug: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_ami_validate`;
};

export const listAwsInstanceTypesPath = (workspaceSlug: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_instance_types`;
};

export const listMachineTypesPath = (workspaceSlug: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/machine_types`;
};

export const listEnvironmentsPath = (workspaceSlug: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/environments`;
};

export const createEnvironmentsPath = (workspaceSlug: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/environments`;
};

export const listImagesPath = (workspaceSlug: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/images`;
};

export const machinePoolsPath = (slug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/v0.1/platform/organization/${slug}/machine_pools${queryParam}`;
};

export const machinePoolPath = (workspaceSlug: string, poolId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/machine_pools/${poolId}`;
};

export const machinesPath = (workspaceSlug: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/machines`;
};

export const machinePath = (workspaceSlug: string, machineId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/machines/${machineId}?force_stop=false`;
};

export const machineRemoteAccessPath = (workspaceSlug: string, machineId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/machines/${machineId}/remote_access`;
};

export const machineLogsPath = (workspaceSlug: string, machineId: string, stage: string, type: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/machines/${machineId}/logs/${stage}/${type}`;
};

export const appsPath = (resource: string, accountSlug: string) => {
  return `/v0.1/${resource}/${accountSlug}/apps`;
};

export const platformLicensePoolsPath = (slug: string, licensePoolId?: string) => {
  return `/v0.1/platform/organization/${slug}/license_pools${licensePoolId ? `/${licensePoolId}` : ''}`;
};

export const manualApprovalBuildListPath = (slug: string) => {
  return `/v0.1/apps/${slug}/build-requests`;
};

export const manualApprovalBuildPath = (appSlug: string, buildRequestSlug: string) => {
  return `/v0.1/apps/${appSlug}/build-requests/${buildRequestSlug}`;
};
